// Shared team-form helpers for the pick screen and the in-game Form table.
//
// See docs/pick-screen-form-requirements.md in the lmsbe repo.
// The fetch half (bulk form lookup + tournament filter workaround) lives in
// src/api/teamFormApi.js.

export const OUTCOME_COLORS = {
  WIN: '#00cc6e',
  DRAW: '#ff9800',
  LOSS: '#ff1744',
};

export const OUTCOME_LETTERS = { WIN: 'W', DRAW: 'D', LOSS: 'L' };

// There used to be an EMPTY_PIP_COLOR here: strips were padded with greyed placeholders up
// to their limit (D4/F17). The owner reversed that on 3 Nov 2026 (backlog item 87) - a strip
// shows the results that exist and nothing else, because grey dots read as clutter rather
// than as "no data". Row heights stay equal through the strip's own min-height, not padding.

export const VENUE_MODES = { OVERALL: 'OVERALL', HOME: 'HOME', AWAY: 'AWAY' };

export const MODE_LABELS = {
  [VENUE_MODES.OVERALL]: 'Last {n}',
  [VENUE_MODES.HOME]: 'Last {n} home',
  [VENUE_MODES.AWAY]: 'Last {n} away',
};

export const MODE_SHORT = {
  [VENUE_MODES.OVERALL]: 'Overall',
  [VENUE_MODES.HOME]: 'Home',
  [VENUE_MODES.AWAY]: 'Away',
};

/**
 * Which collection a mode reads.
 *
 * These were `last5Home` and friends, which stopped being true once `limit` became a
 * parameter - the table asks for ten. The backend renamed them (T15), so this function no
 * longer exists to isolate a misleading name; it just maps mode to field.
 */
function collectionFor(form, mode) {
  if (!form) return [];
  if (mode === VENUE_MODES.HOME) return form.home || [];
  if (mode === VENUE_MODES.AWAY) return form.away || [];
  return form.overall || [];
}

/**
 * How many results the Form table shows for a mode: the last 10 overall, the last 5 home or
 * away (owner, 3 Nov 2026, item 87). Ten venue results reach back about twenty weeks - most of
 * a season - which is a record of a different team from the one playing this week.
 */
export const TABLE_LIMITS = {
  [VENUE_MODES.OVERALL]: 10,
  [VENUE_MODES.HOME]: 5,
  [VENUE_MODES.AWAY]: 5,
};

export function tableLimitFor(mode) {
  return TABLE_LIMITS[mode] || TABLE_LIMITS[VENUE_MODES.OVERALL];
}

/** Entries for a mode, most-recent-first, capped at `limit`. */
export function formEntries(form, mode, limit) {
  return collectionFor(form, mode).slice(0, limit);
}

/** W/D/L tally for a list of entries. */
export function summariseForm(entries) {
  const tally = { W: 0, D: 0, L: 0 };
  (entries || []).forEach((e) => {
    if (e.outcome === 'WIN') tally.W += 1;
    else if (e.outcome === 'DRAW') tally.D += 1;
    else if (e.outcome === 'LOSS') tally.L += 1;
  });
  return tally;
}

/**
 * Which collection a given team's strip should show.
 *
 * In VENUE mode the strip is contextual (V2): the home side shows its home form and
 * the away side its away form. A per-team override wins over the screen mode (V4).
 */
export function resolveMode(screenMode, isHomeTeam, override) {
  if (override) return override;
  if (screenMode === 'VENUE') return isHomeTeam ? VENUE_MODES.HOME : VENUE_MODES.AWAY;
  return VENUE_MODES.OVERALL;
}

/** Cycles a per-team override: Overall -> Home -> Away -> Overall. */
export function nextMode(mode) {
  if (mode === VENUE_MODES.OVERALL) return VENUE_MODES.HOME;
  if (mode === VENUE_MODES.HOME) return VENUE_MODES.AWAY;
  return VENUE_MODES.OVERALL;
}

export function modeLabel(mode, count) {
  return (MODE_LABELS[mode] || MODE_LABELS[VENUE_MODES.OVERALL]).replace('{n}', count);
}
